import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Handshake, User, LogOut, Zap, Settings2, Users2,
  Bell, TrafficCone, Megaphone,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { subRoleLabel, api } from "@/lib/api";
import AskAI from "@/components/AskAI";

const Item = ({ to, Icon, label, tid, end, badge }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={tid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 border-l-2 text-xs uppercase tracking-widest transition-all ${
        isActive
          ? "border-[#39FF14] text-[#39FF14] bg-[#111]"
          : "border-transparent text-neutral-500 hover:text-white hover:bg-[#111]"
      }`
    }
  >
    <Icon size={16} strokeWidth={2} />
    <span className="flex-1">{label}</span>
    {badge > 0 && (
      <span className="bg-[#39FF14] text-black px-1.5 py-0.5 text-[9px] font-bold tabular">{badge}</span>
    )}
  </NavLink>
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = () => api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const isLeader = user?.role === "team_leader";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      <aside className="w-60 border-r border-neutral-900 flex flex-col shrink-0 sticky top-0 h-screen">
        <div
          className="px-5 py-6 border-b border-neutral-900 flex items-center gap-2.5 cursor-pointer"
          onClick={() => navigate("/dashboard")}
          data-testid="sidebar-logo"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_realtor-sync-hub/artifacts/qtyg2nbs_3badc621-45b2-4a92-9947-76cdba8c51d3.png"
            alt="Kreative KPI — Blue to Green to Gold"
            className="w-9 h-9 object-contain"
          />
          <span className="font-display font-black text-sm tracking-tight">KREATIVE KPI</span>
        </div>

        <nav className="flex-1 py-4 flex flex-col overflow-y-auto">
          <div className="px-4 pb-2 text-[10px] uppercase tracking-widest text-neutral-600">Ops</div>
          <Item to="/dashboard" Icon={LayoutDashboard} label="Dashboard" tid="nav-dashboard" end />
          <Item to="/notifications" Icon={Bell} label="Inbox" tid="nav-notifications" badge={unread} />
          <Item to="/kpi-template" Icon={Settings2} label="KPI Template" tid="nav-template" />
          <Item to="/crm" Icon={Users2} label="Agent CRM" tid="nav-crm" />

          <div className="px-4 pt-6 pb-2 text-[10px] uppercase tracking-widest text-neutral-600">Team</div>
          <Item to="/team" Icon={Users} label="My Team" tid="nav-team" />
          {isLeader && (
            <Item to="/traffic-lights" Icon={TrafficCone} label="Traffic Lights" tid="nav-traffic" />
          )}

          <div className="px-4 pt-6 pb-2 text-[10px] uppercase tracking-widest text-neutral-600">Network</div>
          <Item to="/partners" Icon={Handshake} label="Partners" tid="nav-partners" />
          <Item to="/providers" Icon={Megaphone} label="Marketplace" tid="nav-providers" />

          <div className="px-4 pt-6 pb-2 text-[10px] uppercase tracking-widest text-neutral-600">Account</div>
          <Item to="/profile" Icon={User} label="Profile" tid="nav-profile" />
        </nav>

        <div className="border-t border-neutral-900 p-4">
          {user && (
            <div className="flex items-center gap-3 mb-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-none object-cover hairline" />
              ) : (
                <div className="w-9 h-9 bg-[#1a1a1a] hairline flex items-center justify-center text-xs font-bold">
                  {user.name?.[0] || "U"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate">{user.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate">
                  {user.role === "team_leader" ? "Team Leader" : subRoleLabel(user.sub_role)}
                </div>
              </div>
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full hairline flex items-center justify-center gap-2 py-2 text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#39FF14] hover:border-[#39FF14] transition-colors"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      <AskAI />
    </div>
  );
}
